import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import PerpetualOrderDetailModal from './PerpetualOrderDetailModal';
import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  PERPETUAL_ORDER_STATUS_LABELS,
  PERPETUAL_ORDER_STATUS_OPTIONS,
  PERPETUAL_ORDER_TREND_LABELS,
  PERPETUAL_ORDER_TREND_OPTIONS,
  PERPETUAL_PAGE_SCENE,
  buildPerpetualOrderPageParams,
  defaultPerpetualOrderTimeRange,
  formatAmount,
  formatCloseTime,
  formatLeverage,
  formatOptionalPrice,
  formatPhoneEmail,
  formatStakingDays,
  validatePerpetualOrderTimeRange,
} from './perpetualOrderConstants';
import {
  formatSignedProfitLoss,
  orderTrendClassName,
} from '../../utils/orderDisplayFormat';

const DEFAULT_RANGE = defaultPerpetualOrderTimeRange();

const EMPTY_FILTERS = {
  status: '',
  roleId: '',
  orderType: '',
  orderSerialId: '',
  memberIdsCsv: '',
  phoneEmail: '',
  inviterId: '',
  orderTimeFromInput: DEFAULT_RANGE.orderTimeFromInput,
  orderTimeToInput: DEFAULT_RANGE.orderTimeToInput,
  closeTimeFromInput: '',
  closeTimeToInput: '',
};

export default function PerpetualOrdersPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [closing, setClosing] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.perpetualOrdersPage(
        buildPerpetualOrderPageParams(pageNo, nextFilters, PERPETUAL_PAGE_SCENE.ORDER)
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validatePerpetualOrderTimeRange(
      filters.orderTimeFromInput,
      filters.orderTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validatePerpetualOrderTimeRange(
      filters.orderTimeFromInput,
      filters.orderTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      const params = {
        ...buildPerpetualOrderPageParams(page, filters, PERPETUAL_PAGE_SCENE.ORDER),
        exportCurrentPageOnly,
        fileName: 'perpetual_order',
      };
      await adminApi.exportPerpetualOrdersExcel(params);
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  async function handleCloseConfirm() {
    if (!closeTarget) {
      return;
    }
    setClosing(true);
    setError('');
    try {
      await adminApi.closePerpetualOrder(closeTarget.id);
      setCloseTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '平倉失敗');
    } finally {
      setClosing(false);
    }
  }

  return (
    <div className="page-card">
      <h2>永續合約訂單</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="perpetualOrderStatus">訂單狀態</label>
          <select
            id="perpetualOrderStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            {PERPETUAL_ORDER_STATUS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="perpetualOrderType">投注類型</label>
          <select
            id="perpetualOrderType"
            value={filters.orderType}
            onChange={(e) => updateFilter('orderType', e.target.value)}
          >
            {PERPETUAL_ORDER_TREND_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="perpetualRoleId">帳號類型</label>
          <select
            id="perpetualRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="perpetualOrderSerialId">訂單 ID</label>
          <input
            id="perpetualOrderSerialId"
            value={filters.orderSerialId}
            onChange={(e) => updateFilter('orderSerialId', e.target.value)}
            placeholder="請輸入訂單 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="perpetualMemberIds">用戶 ID</label>
          <input
            id="perpetualMemberIds"
            value={filters.memberIdsCsv}
            onChange={(e) => updateFilter('memberIdsCsv', e.target.value)}
            placeholder="請輸入用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="perpetualPhoneEmail">用戶郵箱/手機號</label>
          <input
            id="perpetualPhoneEmail"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
            placeholder="請輸入用戶郵箱/手機號"
          />
        </div>

        <div className="form-field">
          <label htmlFor="perpetualInviterId">直屬上級用戶 ID</label>
          <input
            id="perpetualInviterId"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
            placeholder="請輸入直屬上級用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="perpetualOrderTimeFrom">下單時間起</label>
          <input
            id="perpetualOrderTimeFrom"
            type="datetime-local"
            value={filters.orderTimeFromInput}
            onChange={(e) => updateFilter('orderTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="perpetualOrderTimeTo">下單時間迄</label>
          <input
            id="perpetualOrderTimeTo"
            type="datetime-local"
            value={filters.orderTimeToInput}
            onChange={(e) => updateFilter('orderTimeToInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="perpetualCloseTimeFrom">平倉時間起</label>
          <input
            id="perpetualCloseTimeFrom"
            type="datetime-local"
            value={filters.closeTimeFromInput}
            onChange={(e) => updateFilter('closeTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="perpetualCloseTimeTo">平倉時間迄</label>
          <input
            id="perpetualCloseTimeTo"
            type="datetime-local"
            value={filters.closeTimeToInput}
            onChange={(e) => updateFilter('closeTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（全部資料）'}
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table data-table--wide">
          <thead>
            <tr>
              <th>訂單 ID</th>
              <th>訂單狀態</th>
              <th>用戶 ID</th>
              <th>帳號類型</th>
              <th>用戶郵箱/手機號</th>
              <th>直屬上級用戶 ID</th>
              <th>交易對</th>
              <th>投注類型</th>
              <th>下單金額</th>
              <th>手續費</th>
              <th>保證金</th>
              <th>盈虧金額</th>
              <th>交易前帳戶餘額</th>
              <th>交易後帳戶餘額</th>
              <th>鎖倉週期(天)</th>
              <th>槓桿倍率</th>
              <th>開倉價</th>
              <th>平倉價</th>
              <th>強平價</th>
              <th>止盈價</th>
              <th>止損價</th>
              <th>下單時間</th>
              <th>平倉時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={24} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const profitLoss = formatSignedProfitLoss(row.profitLoss);
                const trendLabel =
                  PERPETUAL_ORDER_TREND_LABELS[row.orderTrend] ||
                  row.orderTrendLabel ||
                  row.orderTrend ||
                  '—';

                return (
                <tr key={row.id || row.orderSerialId}>
                  <td className="mono-cell">{row.orderSerialId || '—'}</td>
                  <td>
                    {PERPETUAL_ORDER_STATUS_LABELS[row.status] || row.statusLabel || row.status || '—'}
                  </td>
                  <td>{row.memberId ?? '—'}</td>
                  <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                  <td>{formatPhoneEmail(row)}</td>
                  <td>{row.inviterId ?? '—'}</td>
                  <td>{row.tradingPair || row.marketSymbolCode || '—'}</td>
                  <td>
                    <span className={orderTrendClassName(row.orderTrend)}>{trendLabel}</span>
                  </td>
                  <td>{formatAmount(row.betAmount)}</td>
                  <td>{formatAmount(row.fee)}</td>
                  <td>{formatAmount(row.margin)}</td>
                  <td className={profitLoss.className}>{profitLoss.text}</td>
                  <td>{formatAmount(row.beforeBalance)}</td>
                  <td>{formatAmount(row.balance)}</td>
                  <td>{formatStakingDays(row.stakingTime)}</td>
                  <td>{formatLeverage(row.contractMultiple)}</td>
                  <td>{formatOptionalPrice(row.orderPrice)}</td>
                  <td>{formatOptionalPrice(row.settlementPrice)}</td>
                  <td>{formatOptionalPrice(row.liquidationPrice)}</td>
                  <td>{formatOptionalPrice(row.takeProfitPrice)}</td>
                  <td>{formatOptionalPrice(row.stopLossPrice)}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>
                    {row.closeTime ? formatDateTime(row.closeTime) : formatCloseTime(row)}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDetailRow(row)}
                      >
                        詳情
                      </button>
                      {row.status === 'OPEN' && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setCloseTarget(row)}
                        >
                          平倉
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={(p) => loadRecords(p, filters)} />

      <PerpetualOrderDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
        title="合約委託單詳情"
      />

      <ConfirmDialog
        open={Boolean(closeTarget)}
        title="確認平倉"
        detail={closeTarget ? `訂單 ${closeTarget.orderSerialId}` : ''}
        message="確定要對此持倉訂單執行平倉？"
        confirmLabel="平倉"
        confirming={closing}
        onConfirm={handleCloseConfirm}
        onClose={() => !closing && setCloseTarget(null)}
      />
    </div>
  );
}
